import { ReactNode } from 'react';
import { TooltipWrapperWithComponentAndState } from '../../../adminComponents/Tooltips/Tooltips';
import { Div } from '../../../styles/styles';
import { Disclaimer, P } from '../../../styles/text';
import { lightTheme } from '../../../styles/theme';

/** Phones have no hover - see below. Same breakpoint as the rest of chat. */
const PHONE_QUERY = '(max-width: 800px)';

/**
 * Who is in the group, on hover.
 *
 * Worn twice: by the thread's title, where the count says how many and this
 * says who, and by the avatar in the inbox, where it answers "is this the group
 * I mean" without opening the conversation and marking it read.
 *
 * Rendered as JSX rather than through the global tooltip's `data-tooltip-html`,
 * because these are user-supplied names and that attribute is injected as raw
 * HTML.
 *
 * Renders its children bare when there is nothing to list - an empty tooltip
 * that follows the cursor around is worse than none - and on a phone, where
 * there is no hover: the only way to reach it there is the tap that opens the
 * thread anyway, so it would flash and leave with the page. The phone check is
 * a plain media query read at render rather than `useIsMobile`, which lives in
 * `helpers/helpers` - a module this folder must never import (see
 * `helpers/appType`).
 */
export const GroupMembersTooltip = ({
  id,
  names,
  children,
}: {
  /** Unique per anchor: this one mounts its own tooltip rather than sharing. */
  id: string;
  names?: string[];
  children: ReactNode;
}) => {
  const isPhone =
    typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches;

  if (isPhone || !names?.length) return <>{children}</>;

  return (
    <TooltipWrapperWithComponentAndState
      id={id}
      $width="auto"
      tooltipWidth="280px"
      tooltip={
        <Div $flex $column $gap="4px">
          <Disclaimer $m="0 0 2px" $color={lightTheme.lightGray}>
            Members
          </Disclaimer>
          {names.map((name) => (
            <P key={name} $m="0" $left>
              {name}
            </P>
          ))}
        </Div>
      }
    >
      {children}
    </TooltipWrapperWithComponentAndState>
  );
};
